import React, { useCallback, useEffect, useRef, useState } from "react";
import { useProducts } from "./product-step/useProducts";
import { useMarket } from "@/hooks/useMarket";
import { CalendarCheck, ChevronRight, Mail } from "lucide-react";
import {
  getInboxLink,
  getProviderName,
  isProviderSupported,
} from "@/utils/emailToLink";
import {
  trackBookingComplete,
  trackMembershipRedemption,
  trackMembershipAddon,
  buildFacebookUserData,
  getBookingItemCategory,
} from "@/utils/analytics";
import { useMembership } from "@/contexts/MembershipContext";
import type { BookingLanguage } from "@/types/booking";
import { t } from "@/locales";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { safeLocalStorage } from "@/utils/safeStorage";
import {
  hasTrackedConversion,
  markConversionTracked,
} from "@/utils/trackedConversions";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { bookingProductType } from "@/utils/bookingProductType";
import { membershipSignupSource } from "@/utils/membershipSignup";
import { bookingCompleteValueCents } from "@/utils/bookingCompleteValue";

interface TimeSlot {
  date: Date;
  time: string;
  isFastBooking?: boolean;
}

interface FormData {
  bookingId?: string;
  total_price?: number;
  discount?: {
    // Present at runtime (BookingForm passes the store discount through) — needed
    // to detect the capped "<firstname>10" referral codes in voucherDiscountCents.
    code?: string;
    type: "percentage" | "fixed" | "fixed_amount";
    value: number;
  };
  email?: string;
  name?: string;
  phone?: string;
  genderAtBirth?: string;
  birthday?: string;
  notes?: string;
  preferredLanguage?: string;
  clinic_id?: string;
  booking_type?: string;
  // Address fields
  street?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string; // ISO code like CH
  anonymousDataUsage?: boolean;
}

interface ConfirmationStepProps {
  timeSlots: TimeSlot[];
  bookingType: "request";
  language: BookingLanguage;
  selectedProducts: string[];
  selectedAddOns?: string[]; // Add selectedAddOns prop
  setBookingId: (id: string) => void;
  formData?: FormData;
  packageComponentMap?: Record<string, string[]>;
}

/**
 * Subtle lavender wash reused from Step 1's recommended membership card
 * (`MEMBERSHIP_GRADIENT` in product-step/BloodTrackingCards.tsx) so the
 * confirmation hero reads as the same premium surface that opens the flow.
 */
const HERO_GRADIENT =
  "linear-gradient(in oklab 127deg, #FAFBFF 0%, #FFFFFF 22%, #F5F8FF 100%)";

const ConfettiBackground = () => {
  // Show confetti immediately without state management delay
  const showConfetti = true;

  // Pre-calculate confetti pieces for better performance
  const confettiPieces = React.useMemo(() => {
    const colors = [
      "#8B5CF6",
      "#A855F7",
      "#9333EA",
      "#7C3AED",
      "#6366F1",
      "#4F46E5",
      "#3B82F6",
      "#06B6D4",
      "#10B981",
      "#84CC16",
    ];
    const pieces = [];

    // More pieces for higher density
    for (let i = 0; i < 60; i++) {
      pieces.push({
        id: `fall-${i}`,
        type: "fall",
        left: Math.random() * 100,
        size: 4 + Math.random() * 4, // Smaller, more uniform sizes (4-8px)
        color: colors[Math.floor(Math.random() * colors.length)],
        duration: 3 + Math.random() * 2,
        delay: Math.random() * 0.1, // Slightly more staggered
        rotation: Math.random() * 360,
        isCircle: Math.random() > 0.7, // Mostly rectangles, some circles
      });
    }

    return pieces;
  }, []);

  console.log("🎉 ConfettiBackground render - showConfetti:", showConfetti);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
      <style>{`
        .confetti-piece {
          position: absolute;
          will-change: transform, opacity;
          backface-visibility: hidden;
          perspective: 1000px;
          top: 0;
          transform: translate3d(0, -120vh, 0);
          opacity: 0;
        }

        @keyframes confetti-fall-optimized {
          0% {
            transform: translate3d(0, -120vh, 0) rotate(0deg);
            opacity: 1;
          }
          5% {
            opacity: 1;
          }
          95% {
            opacity: 1;
          }
          100% {
            transform: translate3d(0, 120vh, 0) rotate(720deg);
            opacity: 0;
          }
        }

        .confetti-fall {
          animation: confetti-fall-optimized var(--duration) linear forwards;
          animation-delay: var(--delay);
          animation-fill-mode: both;
        }
      `}</style>

      {confettiPieces.map((piece) => (
        <div
          key={piece.id}
          className="confetti-piece confetti-fall"
          style={
            {
              left: `${piece.left}%`,
              width: `${piece.size}px`,
              height: piece.isCircle
                ? `${piece.size}px`
                : `${piece.size * 1.2}px`, // Slightly taller rectangles
              backgroundColor: piece.color,
              borderRadius: piece.isCircle ? "50%" : "1px",
              "--duration": `${piece.duration}s`,
              "--delay": `${piece.delay}s`,
              transform: `rotate(${piece.rotation}deg)`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
};

const buildTranslations = (language: BookingLanguage) => {
  return {
    welcome: t(language, "confirmation.welcome"),
    youveGotMail: t(language, "confirmation.youveGotMail"),
    emailSent: t(language, "confirmation.emailSent"),
    emailSentTo: t(language, "confirmation.emailSentTo"),
    checkInbox: t(language, "confirmation.checkInbox"),
    openMailbox: t(language, "confirmation.openMailbox"),
    notConfirmedYet: t(language, "confirmation.notConfirmedYet"),
    whatsNext: t(language, "confirmation.whatsNext"),
    teamReviews: t(language, "confirmation.teamReviews"),
    confirmation: t(language, "confirmation.confirmationByEmail"),
    questionnaire: t(language, "confirmation.questionnaire"),
  };
};

const ConfirmationStep = ({
  timeSlots,
  bookingType,
  language,
  selectedProducts,
  selectedAddOns = [], // Add selectedAddOns with default empty array
  formData,
  packageComponentMap = {},
}: ConfirmationStepProps) => {
  const t = buildTranslations(language);
  const { market } = useMarket();
  const { data } = useProducts(language, market);
  const {
    isMember,
    isEntitledProduct,
    isLoading: isMembershipLoading,
  } = useMembership();
  const [isProcessing, setIsProcessing] = useState(false);
  const hasTrackedBookingComplete = useRef(false);
  const getOrCreateBookingId = useBookingFlowStore(
    (state) => state.getOrCreateBookingId,
  );

  const correctedTimeSlots = timeSlots.map((slot) => {
    const originalDate = slot.date;
    const correctedDate = new Date(
      originalDate.getFullYear(),
      originalDate.getMonth(),
      originalDate.getDate(),
    );
    return {
      date: correctedDate,
      time: slot.time,
      isFastBooking: slot.isFastBooking,
    };
  });

  // Check if any slots are fast booking slots
  const hasFastBookingSlots = correctedTimeSlots.some(
    (slot) => slot.isFastBooking,
  );

  // Set the actual booking type (direct for fast bookings)
  const actualBookingType = hasFastBookingSlots ? "direct" : bookingType;

  // Combine selectedProducts and selectedAddOns for product filtering
  const allSelectedProductIds = [...selectedProducts, ...selectedAddOns];
  console.log(
    "CONFIRMATION STEP - All selected product IDs:",
    allSelectedProductIds,
  );
  console.log("CONFIRMATION STEP - Selected products:", selectedProducts);
  console.log("CONFIRMATION STEP - Selected addons:", selectedAddOns);

  // Get ALL detailed product information based on the combined selected product IDs
  const allSelectedProductDetails =
    data?.products?.filter((p) => allSelectedProductIds.includes(p.id)) || [];

  // product_type label for GTM rules. Marketing computes CAC off this, so a
  // follow-up has to be distinguishable from the booking that won the customer.
  const productType = bookingProductType(allSelectedProductDetails, isMember);

  // Did this booking SELL a membership, and via which path — see
  // membershipSignup.ts for why isMember cannot answer that and why
  // selectedPaymentMode must not be used.
  const membershipCheckoutInitiated = useBookingFlowStore(
    (s) => s.membershipCheckoutInitiated,
  );
  const membershipSignup = membershipSignupSource({
    membershipCheckoutInitiated,
    isMember,
  });

  console.log("CONFIRMATION STEP - product_type:", productType);
  console.log(
    "CONFIRMATION STEP - Selected products:",
    allSelectedProductDetails.map((p) => ({
      name: p.name,
      product_type: p.product_type,
      package_types: p.package_types,
    })),
  );

  // Calculate total price
  const calculateTotalPrice = useCallback(() => {
    // The store discount, in the shape voucherDiscountCents reads.
    const normalizedDiscount = formData?.discount
      ? {
          code: formData.discount.code ?? "",
          type:
            formData.discount.type === "percentage"
              ? ("percentage" as const)
              : ("fixed_amount" as const),
          value: formData.discount.value,
        }
      : null;

    if (formData?.total_price) {
      // total_price is NET for pay-later but PRE-discount for a membership
      // signup — bookingCompleteValue.ts owns that distinction and its tests.
      return bookingCompleteValueCents({
        storedTotalCents: formData.total_price,
        discount: normalizedDiscount,
        membershipSignup,
      });
    }

    const allComponentIds = Object.values(packageComponentMap || {}).flat();
    const displayProducts = allSelectedProductDetails.filter(
      (product) => !allComponentIds.includes(product.id),
    );

    const basePrice = displayProducts.reduce(
      (sum, product) => sum + (product.default_price?.unit_amount || 0),
      0,
    );

    if (normalizedDiscount) {
      // Route through the shared helper so the "<firstname>10" referral cap and
      // whole-franc rounding match the order summary / payment surfaces.
      const discountCents = voucherDiscountCents(normalizedDiscount, basePrice);
      return Math.max(0, basePrice - discountCents);
    }

    return basePrice;
  }, [
    formData,
    packageComponentMap,
    allSelectedProductDetails,
    membershipSignup,
  ]);

  useEffect(() => {
    if (isProcessing) {
      console.log("Notification already sent or processing, skipping...");
      return;
    }

    if (!formData || selectedProducts.length === 0) {
      console.log("Missing essential data, skipping notification");
      return;
    }

    // Membership state decides WHICH conversion event fires (redemption vs
    // paid booking), so reporting before it resolves mislabels a member's
    // booking. `isLoading` is only ever true while a membershipToken is being
    // resolved, so non-members are unaffected. AHD-9067.
    if (isMembershipLoading) {
      console.log("Membership state still resolving, deferring tracking...");
      return;
    }

    console.log("Starting confirmation process with form data:", formData);

    // Track booking completion once per booking. The ref stops repeat effect
    // runs within one mount; the persisted booking id stops a remount (new tab,
    // bookmark, restored session) from re-reporting a booking that was already
    // counted. AHD-9067.
    const bookingId = getOrCreateBookingId();

    if (
      !hasTrackedBookingComplete.current &&
      !hasTrackedConversion(bookingId)
    ) {
      hasTrackedBookingComplete.current = true;
      markConversionTracked(bookingId);

      const totalPrice = calculateTotalPrice();
      const currency = formData?.total_price
        ? allSelectedProductDetails[0]?.default_price?.currency || "CHF"
        : "CHF";

      const ga4Items = allSelectedProductDetails.map((product) => ({
        item_id: product.id,
        item_name: product.name || product.translations?.name || "Unknown",
        item_category:
          product.product_type === "package" ? "Package" : "Add-on",
        price: product.default_price
          ? product.default_price.unit_amount / 100
          : 0,
        currency: product.default_price?.currency || "CHF",
        quantity: 1,
      }));

      // Build Facebook user data for Heimdall pixel
      const facebookUserData = buildFacebookUserData(
        (formData || {}) as Record<string, unknown>,
      );

      // Only treat as a redemption if at least one booked product is actually
      // entitled (available in membership). If the member has used all
      // entitlements, fall through to regular booking_complete.
      const hasEntitledItem = allSelectedProductDetails.some((p) =>
        isEntitledProduct(p.stripe_product_id),
      );

      const sharedProps = {
        booking_type: actualBookingType,
        product_type: productType,
        // "cadence" (path A, joined from the packages page) | "upsell"
        // (path B, joined on the details step) | "none". Segments the
        // acquisition buckets WITHOUT moving bookings between them:
        // product_type keeps saying what was booked, and a signup keeps
        // counting as the acquisition it is.
        membership_signup: membershipSignup,
        clinic_id: formData.clinic_id,
        item_category: getBookingItemCategory(
          allSelectedProductIds,
          allSelectedProductDetails,
          isMember && hasEntitledItem && totalPrice === 0,
        ),
        email: formData.email,
        ...facebookUserData,
      };

      if (isMember && hasEntitledItem) {
        trackMembershipRedemption(ga4Items, bookingId, sharedProps);

        // Any non-zero total means the member paid for extras on top of
        // their free entitlement — PSA, LPA, MRI add-ons, etc.
        //
        // The redemption above and this add-on both map to a GA4 `purchase` in
        // GTM (GTM-M8X7HHCQ, "Send ecommerce data" from the Data Layer), and
        // GA4 dedupes purchases by `transaction_id`. They fire for the same
        // booking milliseconds apart, so a shared id would make GA4 drop the
        // second — this paid add-on, silently losing its revenue. Suffix the
        // add-on id so both purchases are counted. AHD-9067.
        if (totalPrice > 0) {
          trackMembershipAddon(
            ga4Items,
            totalPrice / 100,
            `${bookingId}-addon`,
            currency,
            sharedProps,
          );
        }
      } else {
        trackBookingComplete(
          ga4Items,
          totalPrice / 100,
          bookingId,
          currency,
          sharedProps,
        );
      }
    }

    setIsProcessing(true);
  }, [
    isProcessing,
    isMembershipLoading,
    getOrCreateBookingId,
    formData?.email,
    formData?.name,
    formData?.clinic_id,
    selectedProducts.join(","),
    allSelectedProductDetails,
    calculateTotalPrice,
    actualBookingType,
  ]);

  // Clean up localStorage after successful booking
  const clearCart = useBookingFlowStore((state) => state.clearCart);

  useEffect(() => {
    // Clear email capture related data
    safeLocalStorage.removeItem("userEmail");
    safeLocalStorage.removeItem("emailCaptureSkipped");
    // Clear form data that was saved during step 5 (details)
    safeLocalStorage.removeItem("bookingFormData");
    // Clear the booking cart from localStorage
    clearCart();
    console.log("✓ localStorage cleaned up after booking (including cart)");
  }, [clearCart]); // Run once on mount

  useEffect(() => {
    // Trap browser back: booking is final and the server session has been
    // cleared. Allowing back into the flow would let a user resubmit the
    // same entitlement with stale client state.
    const trapState = { _confirmationTrap: true };
    try {
      window.history.pushState(trapState, "", window.location.href);
    } catch {
      /* sandboxed iframe / cross-origin / Safari pushState rate limit */
    }

    const onPopState = () => {
      try {
        window.history.pushState(trapState, "", window.location.href);
      } catch {
        /* see above */
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col bg-gray-50">
      {/* Premium lavender wash behind the hero, reused from Step 1's
          recommended membership card. Fades into the gray-50 body below. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[60vh]"
        style={{
          backgroundImage: HERO_GRADIENT,
          maskImage: "linear-gradient(to bottom, black 60%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, black 60%, transparent)",
        }}
      />

      <ConfettiBackground />

      {/* Main content - centered on tall screens, scrollable on short ones */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center min-h-0">
        {/* Calendar Icon */}
        <div className="mb-12">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto bg-light-blue-200">
            <CalendarCheck
              className="w-12 h-12 text-deep-blue-900"
              strokeWidth={1.5}
            />
          </div>
        </div>

        {/* Welcome Message */}
        <h1 className="text-2xl font-light text-gray-900 leading-relaxed max-w-md font-sans px-6">
          {t.welcome}
        </h1>

        {/* A requested slot is only a wish until the clinic confirms it; a fast
            booking slot is confirmed on the spot, so it gets no caveat. AHD-13120 */}
        {!hasFastBookingSlots && (
          <p className="mt-4 text-base text-gray-700 max-w-md font-sans px-6">
            {t.notConfirmedYet}
          </p>
        )}

        {/* Email Confirmation Notice */}
        {formData?.email && (
          <div className="mt-8 mb-12 max-w-md mx-auto px-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100  shadow-md">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail
                    className="w-5 h-5 text-aubergine-800"
                    strokeWidth={2}
                  />
                </div>
                <h3 className="text-lg font-normal text-gray-900 font-sans">
                  {t.youveGotMail}
                </h3>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600 font-sans mb-4">
                  {t.emailSentTo}{" "}
                  <span className="font-medium text-gray-900">
                    {formData.email}
                  </span>
                </p>

                {isProviderSupported(formData.email) &&
                  getInboxLink(formData.email) && (
                    <a
                      href={getInboxLink(formData.email)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#4C2652] to-[#5C4279] text-white rounded-lg font-sans text-sm hover:shadow-md transition-all"
                    >
                      {t.openMailbox.replace(
                        "%s",
                        getProviderName(formData.email) || t.checkInbox,
                      )}
                    </a>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* What's Next Section - Bottom */}
      <div className="relative z-10 px-6 pb-12">
        <div className="text-center space-y-5">
          <h2 className="text-lg font-normal text-gray-900 font-sans">
            {t.whatsNext}
          </h2>

          <div className="space-y-4 max-w-md mx-auto text-center">
            {/* Step 1 */}
            <div className="inline-flex items-start">
              <ChevronRight
                className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5 mr-1"
                strokeWidth={2}
              />
              <span className="text-gray-700 font-normal font-sans">
                {t.teamReviews}
              </span>
            </div>

            {/* Step 2 */}
            <div className="inline-flex items-start">
              <ChevronRight
                className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5 mr-1"
                strokeWidth={2}
              />
              <span className="text-gray-700 font-normal font-sans">
                {t.confirmation}
              </span>
            </div>

            {/* Step 3 - Questionnaire for all booking types */}
            <div className="inline-flex items-start">
              <ChevronRight
                className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5 mr-1"
                strokeWidth={2}
              />
              <span className="text-gray-700 font-normal font-sans">
                {t.questionnaire}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationStep;
